import { XMLParser, XMLValidator } from 'fast-xml-parser';
import { tokenizeXPath } from './xml-grammar.js';
function isRawNode(value) {
    return typeof value === 'object' && value !== null;
}
// R-38 — fast-xml-parser preserves the XML declaration (`<?xml … ?>`) and
// other processing instructions as top-level nodes when preserveOrder is on.
// SampleTreePane used to pick the `?xml` PI as the root and render a blank
// tree. Strip the leading PI(s) before parsing and also defensively skip any
// PI/comment-shaped key when locating the document root.
const LEADING_PI_RE = /^\s*(?:<\?[\s\S]*?\?>\s*)+/;
function isProcessingInstructionKey(key) {
    // fast-xml-parser keys for PIs/comments begin with '?' or '!' (e.g. '?xml').
    return key.startsWith('?') || key.startsWith('!');
}
export function parseXML(raw) {
    // Strip leading processing instructions + whitespace so the XML declaration
    // (`<?xml version="1.0" encoding="UTF-8"?>`) doesn't get treated as the root.
    const stripped = raw.replace(LEADING_PI_RE, '');
    const valid = XMLValidator.validate(stripped);
    if (valid !== true) {
        const err = valid.err;
        return { ok: false, error: err?.msg ?? 'invalid xml' };
    }
    const parser = new XMLParser({
        ignoreAttributes: false,
        attributeNamePrefix: '',
        attributesGroupName: '@',
        preserveOrder: true,
        trimValues: true,
    });
    const parsed = parser.parse(stripped);
    const nodes = Array.isArray(parsed) ? parsed : [parsed];
    const first = nodes.find((n) => {
        if (!isRawNode(n))
            return false;
        if ('#text' in n)
            return false;
        // Belt-and-suspenders: ignore any residual PI/comment node even if the
        // regex above misses it (e.g. an unusual whitespace edge case).
        const keys = Object.keys(n).filter((k) => k !== ':@' && k !== '@');
        return keys.length > 0 && keys.some((k) => !isProcessingInstructionKey(k));
    });
    if (!first)
        return { ok: false, error: 'no root element' };
    return { ok: true, doc: { root: convert(first) } };
}
function convert(node) {
    const entries = Object.entries(node);
    let name = '';
    let children = [];
    for (const [k, v] of entries) {
        if (k === '@' || k === ':@')
            continue;
        if (k === '#text')
            continue;
        // R-38 — skip residual processing-instruction / comment keys.
        if (isProcessingInstructionKey(k))
            continue;
        name = k;
        children = Array.isArray(v) ? v : [v];
        break;
    }
    const attrs = {};
    // In preserveOrder mode with attributesGroupName:'@', attrs live at node[':@'][<group>].
    // Unwrap one level if the container holds a single group object (e.g. { '@': { class: 'INV' } }).
    const rawContainer = node[':@'] ?? node['@'];
    let attrSource;
    if (isRawNode(rawContainer)) {
        const inner = rawContainer['@'];
        attrSource = isRawNode(inner) ? inner : rawContainer;
    }
    if (attrSource) {
        for (const [ak, av] of Object.entries(attrSource)) {
            attrs[ak] = String(av);
        }
    }
    const childElems = [];
    let text = null;
    for (const c of children) {
        if (!isRawNode(c))
            continue;
        if ('#text' in c) {
            const t = c['#text'];
            text = text === null ? String(t) : text + String(t);
        }
        else {
            childElems.push(convert(c));
        }
    }
    return { name, attrs, children: childElems, text };
}
export function evaluateXPath(doc, xpath) {
    const tok = tokenizeXPath(xpath);
    if ('error' in tok)
        return { found: false, error: tok.error };
    let cursor = [doc.root];
    for (let i = 0; i < tok.tokens.length; i++) {
        const t = tok.tokens[i];
        if (!t)
            continue;
        if (t.kind === 'attr') {
            const current = cursor[0];
            if (!current)
                return { found: false };
            const v = current.attrs[t.name];
            return v === undefined ? { found: false } : { found: true, value: v };
        }
        if (i === 0) {
            if (doc.root.name !== t.name)
                return { found: false };
            if (t.index !== undefined && t.index !== 1)
                return { found: false };
            cursor = [doc.root];
            continue;
        }
        const current = cursor[0];
        if (!current)
            return { found: false };
        const matching = current.children.filter((c) => c.name === t.name);
        if (t.index !== undefined) {
            const el = matching[t.index - 1];
            if (!el)
                return { found: false };
            cursor = [el];
        }
        else {
            const first = matching[0];
            if (!first)
                return { found: false };
            cursor = [first];
        }
    }
    const final = cursor[0];
    if (!final)
        return { found: false };
    return { found: true, value: final.text ?? '' };
}
//# sourceMappingURL=xml-parse.js.map